import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { toast } from '../components/Toast';
import { getMyInquiryDetail, acceptInquiryQuote } from '../api/inquiry.api';
import InquiryChat from '../components/InquiryChat';

// Fix leaflet default marker icons (CDN fallback)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const STATUS_CONFIG = {
    new:              { label: 'New',           cls: 'bg-blue-100 text-blue-800',     icon: 'ri-time-line' },
    in_discussion:    { label: 'In Discussion', cls: 'bg-amber-100 text-amber-800',   icon: 'ri-discuss-line' },
    quoted:           { label: 'Quoted',        cls: 'bg-purple-100 text-purple-800', icon: 'ri-price-tag-3-line' },
    accepted:         { label: 'Accepted',      cls: 'bg-green-100 text-green-800',   icon: 'ri-checkbox-circle-line' },
    closed_converted: { label: 'Converted',     cls: 'bg-emerald-100 text-emerald-800', icon: 'ri-check-double-line' },
    closed_rejected:  { label: 'Rejected',      cls: 'bg-muted text-muted-foreground',  icon: 'ri-close-circle-line' },
};

const TIMELINE_COLOR = {
    new:              'bg-blue-500',
    in_discussion:    'bg-amber-500',
    quoted:           'bg-purple-500',
    accepted:         'bg-green-500',
    closed_converted: 'bg-emerald-500',
    closed_rejected:  'bg-muted-foreground',
};

const PAYMENT_STATUS_CONFIG = {
    paid:    { label: 'Paid',    cls: 'bg-green-100 text-green-800' },
    pending: { label: 'Pending', cls: 'bg-yellow-100 text-yellow-800' },
    failed:  { label: 'Failed',  cls: 'bg-red-100 text-red-800' },
};

function StatusBadge({ status }) {
    const cfg = STATUS_CONFIG[status] || { label: status, cls: 'bg-muted text-muted-foreground', icon: 'ri-question-line' };
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${cfg.cls}`}>
            <i className={cfg.icon}></i>
            {cfg.label}
        </span>
    );
}

function DeliveryMap({ latitude, longitude }) {
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) return null;

    return (
        <div className="rounded-lg overflow-hidden border border-border mt-3" style={{ height: '200px' }}>
            <MapContainer
                center={[lat, lng]}
                zoom={14}
                style={{ height: '100%', width: '100%' }}
                zoomControl={false}
                dragging={false}
                scrollWheelZoom={false}
                doubleClickZoom={false}
                touchZoom={false}
                attributionControl={false}
            >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[lat, lng]} />
            </MapContainer>
        </div>
    );
}

const formatKES = (v) => 'KES ' + Number(v || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 });
const formatDate = (d) => new Date(d).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' });
const formatDateTime = (d) => new Date(d).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

export default function BulkInquiryDetail() {
    const { inquiryNumber } = useParams();
    const navigate = useNavigate();
    const [inquiry, setInquiry] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showChatModal, setShowChatModal] = useState(false);
    const [accepting, setAccepting] = useState(false);
    const [showAcceptModal, setShowAcceptModal] = useState(false);

    useEffect(() => {
        document.title = `Inquiry ${inquiryNumber} | Fone Express`;
    }, [inquiryNumber]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await getMyInquiryDetail(inquiryNumber);
                if (res.data?.isOk) {
                    setInquiry(res.data.data);
                } else {
                    toast.error('Inquiry not found');
                    navigate('/account/bulk-inquiries');
                }
            } catch {
                toast.error('Failed to load inquiry');
                navigate('/account/bulk-inquiries');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [inquiryNumber, navigate]);

    const handleAcceptQuote = async () => {
        setAccepting(true);
        try {
            const res = await acceptInquiryQuote(inquiryNumber);
            if (res.data?.isOk) {
                toast.success('Quote accepted! Our team will be in touch shortly.');
                setShowAcceptModal(false);
                // Reload inquiry to reflect new status
                const updated = await getMyInquiryDetail(inquiryNumber);
                if (updated.data?.isOk) setInquiry(updated.data.data);
            } else {
                toast.error(res.data?.message || 'Failed to accept quote');
            }
        } catch {
            toast.error('Failed to accept quote. Please try again.');
        } finally {
            setAccepting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-4 border-border border-t-primary rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!inquiry) return null;

    const isAccepted = ['accepted', 'closed_converted'].includes(inquiry.status);
    const isRejected = inquiry.status === 'closed_rejected';
    const isConverted = inquiry.status === 'closed_converted';
    const hasQuote = !!inquiry.quote?.quoteNumber;
    const isClosed = ['closed_converted', 'closed_rejected'].includes(inquiry.status);
    const hasCoords = inquiry.deliveryCoordinates?.latitude && inquiry.deliveryCoordinates?.longitude;
    const order = inquiry.orderId;

    return (
        <div className="space-y-5">
            {/* Back + header */}
            <div>
                <button
                    onClick={() => navigate('/account/bulk-inquiries')}
                    className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
                >
                    <i className="ri-arrow-left-line"></i>
                    Back to Inquiries
                </button>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <h1 className="text-xl font-bold text-foreground">Inquiry #{inquiry.inquiryNumber}</h1>
                        <p className="text-sm text-muted-foreground mt-0.5">
                            Submitted {formatDateTime(inquiry.createdAt)}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <StatusBadge status={inquiry.status} />
                        {inquiry.status === 'quoted' && (
                            <button
                                onClick={() => setShowAcceptModal(true)}
                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
                            >
                                <i className="ri-checkbox-circle-line"></i>
                                Accept Quote
                            </button>
                        )}
                        <button
                            onClick={() => setShowChatModal(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                        >
                            <i className="ri-chat-3-line"></i>
                            Chat
                        </button>
                    </div>
                </div>
            </div>

            {/* Quote ready banner */}
            {inquiry.status === 'quoted' && (
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3">
                        <i className="ri-price-tag-3-fill text-purple-600 text-xl flex-shrink-0 mt-0.5"></i>
                        <div>
                            <p className="font-semibold text-purple-800 dark:text-purple-300">Your Quote is Ready</p>
                            <p className="text-sm text-purple-700 dark:text-purple-400 mt-0.5">
                                Review the quote below and accept it to proceed with your order.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowAcceptModal(true)}
                        className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-green-600 text-white hover:bg-green-700 transition-colors shadow-sm"
                    >
                        <i className="ri-checkbox-circle-line"></i>
                        Accept Quote
                    </button>
                </div>
            )}

            {/* Accepted banner */}
            {isAccepted && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-green-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <div>
                        <p className="font-semibold text-green-800 dark:text-green-300">
                            {isConverted ? 'Order Created' : 'Quote Accepted'}
                        </p>
                        <p className="text-sm text-green-700 dark:text-green-400 mt-0.5">
                            {isConverted
                                ? 'Your inquiry has been converted to an order. Our team will be in touch.'
                                : 'You have accepted the quote. Our team will contact you to finalise the order.'}
                        </p>
                    </div>
                </div>
            )}

            {/* Rejected banner */}
            {isRejected && (
                <div className="bg-muted border border-border rounded-xl p-4 flex items-start gap-3">
                    <i className="ri-close-circle-line text-muted-foreground text-xl flex-shrink-0 mt-0.5"></i>
                    <div>
                        <p className="font-semibold text-foreground">Inquiry Closed</p>
                        <p className="text-sm text-muted-foreground mt-0.5">
                            This inquiry was not able to proceed. Please submit a new inquiry if you'd like to try again.
                        </p>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Main content */}
                <div className="lg:col-span-2 space-y-5">
                    {/* Company & Contact */}
                    <div className="bg-card border border-border rounded-xl p-5">
                        <h2 className="text-sm font-semibold text-foreground mb-4">Company & Contact</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <div>
                                <p className="text-xs text-muted-foreground mb-0.5">Company</p>
                                <p className="font-medium text-foreground">{inquiry.companyName}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground mb-0.5">Contact Person</p>
                                <p className="font-medium text-foreground">{inquiry.contactName}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground mb-0.5">Email</p>
                                <p className="font-medium text-foreground">{inquiry.email}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground mb-0.5">Phone</p>
                                <p className="font-medium text-foreground">{inquiry.phone}</p>
                            </div>
                            {inquiry.expectedDeliveryDate && (
                                <div>
                                    <p className="text-xs text-muted-foreground mb-0.5">Expected Delivery</p>
                                    <p className="font-medium text-foreground">{formatDate(inquiry.expectedDeliveryDate)}</p>
                                </div>
                            )}
                            {inquiry.specialRequirements && (
                                <div className="sm:col-span-2">
                                    <p className="text-xs text-muted-foreground mb-0.5">Special Requirements</p>
                                    <p className="font-medium text-foreground">{inquiry.specialRequirements}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Delivery Location */}
                    <div className="bg-card border border-border rounded-xl p-5">
                        <h2 className="text-sm font-semibold text-foreground mb-3">Delivery Location</h2>
                        <div className="flex items-start gap-2 text-sm">
                            <i className="ri-map-pin-line text-primary mt-0.5 flex-shrink-0"></i>
                            <div>
                                <p className="font-medium text-foreground">{inquiry.targetDeliveryLocation}</p>
                                {hasCoords && (
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {inquiry.deliveryCoordinates.latitude.toFixed(6)}, {inquiry.deliveryCoordinates.longitude.toFixed(6)}
                                    </p>
                                )}
                            </div>
                        </div>
                        {hasCoords && (
                            <DeliveryMap
                                latitude={inquiry.deliveryCoordinates.latitude}
                                longitude={inquiry.deliveryCoordinates.longitude}
                            />
                        )}
                    </div>

                    {/* Requested Items */}
                    {inquiry.items?.length > 0 && (
                        <div className="bg-card border border-border rounded-xl p-5">
                            <h2 className="text-sm font-semibold text-foreground mb-4">
                                Requested Items ({inquiry.items.length})
                            </h2>
                            <div className="space-y-3">
                                {inquiry.items.map((item, i) => (
                                    <div key={i} className="flex items-center gap-3 p-3 bg-muted/40 rounded-lg border border-border">
                                        <div className="w-12 h-12 flex-shrink-0 bg-background rounded-lg border border-border overflow-hidden">
                                            {item.imageUrl ? (
                                                <img src={item.imageUrl} alt={item.productName} className="w-full h-full object-contain p-1" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <i className="ri-box-3-line text-muted-foreground/40 text-lg"></i>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-foreground truncate">{item.productName}</p>
                                            {item.sku && <p className="text-xs text-muted-foreground">{item.sku}</p>}
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <p className="text-sm font-semibold text-foreground">Qty: {item.quantity}</p>
                                            {item.unitPrice > 0 && (
                                                <p className="text-xs text-muted-foreground">{formatKES(item.unitPrice)} / unit</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Quote Details */}
                    {hasQuote && (
                        <div className="bg-card border border-border rounded-xl p-5">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-sm font-semibold text-foreground">
                                    Quote — {inquiry.quote.quoteNumber}
                                </h2>
                                {inquiry.quote.validUntil && (
                                    <span className="text-xs text-muted-foreground">
                                        Valid until {formatDate(inquiry.quote.validUntil)}
                                    </span>
                                )}
                            </div>

                            {/* Quote items */}
                            <div className="overflow-x-auto mb-4">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-border">
                                            <th className="text-left py-2 text-xs text-muted-foreground font-medium">Product</th>
                                            <th className="text-center py-2 text-xs text-muted-foreground font-medium">Qty</th>
                                            <th className="text-right py-2 text-xs text-muted-foreground font-medium">Unit Price</th>
                                            <th className="text-right py-2 text-xs text-muted-foreground font-medium">Subtotal</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                        {inquiry.quote.items?.map((item, i) => (
                                            <tr key={i}>
                                                <td className="py-2.5 pr-4 text-foreground">
                                                    {item.productName}
                                                    {item.sku && <span className="ml-1 text-xs text-muted-foreground">({item.sku})</span>}
                                                </td>
                                                <td className="py-2.5 text-center text-foreground">{item.quantity}</td>
                                                <td className="py-2.5 text-right text-foreground">{formatKES(item.discountedUnitPrice)}</td>
                                                <td className="py-2.5 text-right font-medium text-foreground">{formatKES(item.subtotal)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {inquiry.quote.notes && (
                                <p className="pt-3 border-t border-border text-xs text-muted-foreground">
                                    <span className="font-medium">Notes: </span>{inquiry.quote.notes}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className="space-y-5">
                    {/* Price Summary */}
                    {hasQuote && (
                        <div className="bg-card border border-border rounded-xl p-5">
                            <h2 className="text-sm font-semibold text-foreground mb-4">Price Summary</h2>
                            <div className="space-y-1.5">
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Subtotal</span>
                                    <span className="text-foreground">{formatKES(inquiry.quote.subtotal)}</span>
                                </div>
                                {inquiry.quote.totalDiscount > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Discount</span>
                                        <span className="text-green-600">–{formatKES(inquiry.quote.totalDiscount)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Shipping</span>
                                    <span className="text-foreground">{formatKES(inquiry.quote.shipping)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Tax ({inquiry.quote.taxRate}%)</span>
                                    <span className="text-foreground">{formatKES(inquiry.quote.taxAmount)}</span>
                                </div>
                                <div className="flex justify-between text-base font-bold pt-2 border-t border-border">
                                    <span className="text-foreground">Grand Total</span>
                                    <span className="text-primary">{formatKES(inquiry.quote.grandTotal)}</span>
                                </div>
                            </div>

                            {/* Payment info when converted */}
                            {isConverted && order && (
                                <div className="mt-4 pt-4 border-t border-border space-y-2">
                                    <p className="text-xs font-semibold text-foreground mb-2">Payment Details</p>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-muted-foreground">Method</span>
                                        <span className="text-foreground capitalize">
                                            {(order.paymentMethod || 'Invoice').replace(/_/g, ' ')}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-muted-foreground">Status</span>
                                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${PAYMENT_STATUS_CONFIG[order.paymentStatus]?.cls || 'bg-muted text-muted-foreground'}`}>
                                            {PAYMENT_STATUS_CONFIG[order.paymentStatus]?.label || order.paymentStatus || 'Pending'}
                                        </span>
                                    </div>
                                    {order.pricing?.grandTotal > 0 && (
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-muted-foreground">Amount Due</span>
                                            <span className="font-semibold text-foreground">{formatKES(order.pricing.grandTotal)}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Converted Order card */}
                    {isConverted && order && (
                        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-5">
                            <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                                <i className="ri-shopping-bag-3-line text-emerald-600"></i>
                                Converted Order
                            </h2>
                            <div className="space-y-3 text-sm">
                                <div>
                                    <p className="text-xs text-muted-foreground mb-0.5">Order Number</p>
                                    <p className="font-semibold text-foreground">#{order.orderNumber}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground mb-0.5">Order Status</p>
                                    <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-800 capitalize">
                                        {(order.status || '').replace(/_/g, ' ')}
                                    </span>
                                </div>
                                {order.createdAt && (
                                    <div>
                                        <p className="text-xs text-muted-foreground mb-0.5">Created</p>
                                        <p className="text-foreground">{formatDateTime(order.createdAt)}</p>
                                    </div>
                                )}
                                {order.shippingAddress && (
                                    <div>
                                        <p className="text-xs text-muted-foreground mb-0.5">Shipping Address</p>
                                        <p className="text-foreground text-xs leading-relaxed">
                                            {[
                                                order.shippingAddress.fullName,
                                                order.shippingAddress.addressLine1,
                                                order.shippingAddress.addressLine2,
                                                order.shippingAddress.city,
                                                order.shippingAddress.county,
                                            ].filter(Boolean).join(', ')}
                                        </p>
                                        {order.shippingAddress.phone && (
                                            <p className="text-xs text-muted-foreground mt-0.5">{order.shippingAddress.phone}</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Status Timeline */}
                    <div className="bg-card border border-border rounded-xl p-5">
                        <h2 className="text-sm font-semibold text-foreground mb-4">Status Timeline</h2>
                        {inquiry.statusHistory?.length > 0 ? (
                            <ol className="relative border-l border-border ml-2 space-y-4">
                                {[...inquiry.statusHistory].reverse().map((h, i) => (
                                    <li key={i} className="pl-5 relative">
                                        <span className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${TIMELINE_COLOR[h.status] || 'bg-muted-foreground'}`}></span>
                                        <p className="text-xs font-medium text-foreground capitalize">
                                            {(h.status || '').replace(/_/g, ' ')}
                                        </p>
                                        {h.note && <p className="text-xs text-muted-foreground mt-0.5">{h.note}</p>}
                                        <p className="text-xs text-muted-foreground mt-0.5">{formatDateTime(h.timestamp)}</p>
                                    </li>
                                ))}
                            </ol>
                        ) : (
                            <p className="text-sm text-muted-foreground">No history yet.</p>
                        )}
                    </div>

                    {/* What happens next */}
                    {!isClosed && (
                        <div className="bg-muted/50 border border-border rounded-xl p-5">
                            <h2 className="text-sm font-semibold text-foreground mb-3">What Happens Next?</h2>
                            <ul className="space-y-2.5 text-sm text-muted-foreground">
                                {inquiry.status === 'new' && (
                                    <>
                                        <li className="flex items-start gap-2"><i className="ri-check-line text-primary mt-0.5 flex-shrink-0"></i>Inquiry received</li>
                                        <li className="flex items-start gap-2"><i className="ri-time-line text-muted-foreground/50 mt-0.5 flex-shrink-0"></i>Our team reviews your request</li>
                                        <li className="flex items-start gap-2"><i className="ri-time-line text-muted-foreground/50 mt-0.5 flex-shrink-0"></i>We prepare a custom quote</li>
                                        <li className="flex items-start gap-2"><i className="ri-time-line text-muted-foreground/50 mt-0.5 flex-shrink-0"></i>Quote sent to your email</li>
                                    </>
                                )}
                                {inquiry.status === 'in_discussion' && (
                                    <>
                                        <li className="flex items-start gap-2"><i className="ri-check-line text-primary mt-0.5 flex-shrink-0"></i>Under review</li>
                                        <li className="flex items-start gap-2"><i className="ri-time-line text-muted-foreground/50 mt-0.5 flex-shrink-0"></i>We prepare a custom quote</li>
                                        <li className="flex items-start gap-2"><i className="ri-time-line text-muted-foreground/50 mt-0.5 flex-shrink-0"></i>Quote sent to your email</li>
                                    </>
                                )}
                                {inquiry.status === 'quoted' && (
                                    <>
                                        <li className="flex items-start gap-2"><i className="ri-check-line text-primary mt-0.5 flex-shrink-0"></i>Quote prepared</li>
                                        <li className="flex items-start gap-2"><i className="ri-mail-line text-primary mt-0.5 flex-shrink-0"></i>Check your email to accept the quote</li>
                                        <li className="flex items-start gap-2"><i className="ri-time-line text-muted-foreground/50 mt-0.5 flex-shrink-0"></i>Order will be created on acceptance</li>
                                    </>
                                )}
                                {inquiry.status === 'accepted' && (
                                    <>
                                        <li className="flex items-start gap-2"><i className="ri-check-line text-primary mt-0.5 flex-shrink-0"></i>Quote accepted</li>
                                        <li className="flex items-start gap-2"><i className="ri-time-line text-muted-foreground/50 mt-0.5 flex-shrink-0"></i>Our team finalises your order</li>
                                    </>
                                )}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {/* Accept Quote Confirmation Modal */}
            {showAcceptModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={() => !accepting && setShowAcceptModal(false)}
                    ></div>
                    <div className="relative bg-card rounded-xl w-full max-w-md shadow-xl p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
                            </div>
                            <div>
                                <h3 className="text-base font-semibold text-foreground">Accept Quote</h3>
                                <p className="text-xs text-muted-foreground">Quote {inquiry.quote?.quoteNumber}</p>
                            </div>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                            You are about to accept the quote for:
                        </p>
                        <div className="bg-muted/50 rounded-lg p-3 mb-4 space-y-1">
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Items</span>
                                <span className="font-medium text-foreground">{inquiry.quote?.items?.length || 0}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Grand Total</span>
                                <span className="font-bold text-primary">{formatKES(inquiry.quote?.grandTotal)}</span>
                            </div>
                            {inquiry.quote?.validUntil && (
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Valid Until</span>
                                    <span className="text-foreground">{formatDate(inquiry.quote.validUntil)}</span>
                                </div>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground mb-5">
                            By accepting, you agree to proceed with this quote. Our team will contact you to finalise payment and delivery.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowAcceptModal(false)}
                                disabled={accepting}
                                className="flex-1 px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAcceptQuote}
                                disabled={accepting}
                                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {accepting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        Accepting…
                                    </>
                                ) : (
                                    <>
                                        <i className="ri-checkbox-circle-line"></i>
                                        Confirm Accept
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Chat Modal */}
            {showChatModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={() => setShowChatModal(false)}
                    ></div>
                    <div className="relative bg-card rounded-xl w-full max-w-lg shadow-xl flex flex-col overflow-hidden" style={{ height: '580px' }}>
                        <div className="px-5 py-3 border-b border-border flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <i className="ri-chat-3-line text-primary"></i>
                                <div>
                                    <h2 className="text-sm font-semibold text-foreground">Chat with Support</h2>
                                    <p className="text-xs text-muted-foreground">Inquiry #{inquiry.inquiryNumber}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowChatModal(false)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                            >
                                <i className="ri-close-line text-lg"></i>
                            </button>
                        </div>
                        <div className="flex-1 min-h-0">
                            <InquiryChat
                                inquiryNumber={inquiry.inquiryNumber}
                                isClosed={isClosed}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
